/**
 * AttachmentChecklist Component
 * 
 * Reminder checklist for documents to attach to the appeal letter.
 * Not file upload - just a checklist to ensure completeness.
 */

import React from 'react';
import {
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Paper,
  Stack,
  Chip,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  AttachFile as AttachIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { ProcedureCategory, DenialCategory } from '../../constants/appealTemplates';

export interface AttachmentItem {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  required: boolean;
  suggestedFor?: {
    procedures?: ProcedureCategory[];
    denials?: DenialCategory[];
  };
}

interface AttachmentChecklistProps {
  value: AttachmentItem[];
  onChange: (items: AttachmentItem[]) => void;
  procedureCategory?: ProcedureCategory;
  denialCategory?: DenialCategory;
  disabled?: boolean;
}

const DEFAULT_ATTACHMENTS: Omit<AttachmentItem, 'checked'>[] = [
  {
    id: 'op-report',
    label: 'Operative Report',
    description: 'Detailed surgical report including findings and procedure details',
    required: true,
  },
  {
    id: 'medical-records',
    label: 'Medical Records',
    description: 'Relevant clinical notes, H&P, progress notes',
    required: true,
  },
  {
    id: 'imaging',
    label: 'Imaging Studies',
    description: 'MRI, CT, X-ray reports supporting diagnosis',
    required: false,
    suggestedFor: {
      procedures: ['Spine', 'Arthroscopy'],
    },
  },
  {
    id: 'emg',
    label: 'EMG / Nerve Conduction Study',
    description: 'Electrodiagnostic study results',
    required: false,
    suggestedFor: {
      procedures: ['Nerve'],
    },
  },
  {
    id: 'pathology',
    label: 'Pathology Report',
    description: 'If tissue samples were taken',
    required: false,
  },
  {
    id: 'prior-auth',
    label: 'Prior Authorization Documentation',
    description: 'Auth approval letter, reference numbers, communications',
    required: false,
    suggestedFor: {
      denials: ['Authorization', 'Administrative', 'SiteOfCare'],
    },
  },
  {
    id: 'pt-records',
    label: 'Physical Therapy Records',
    description: 'Documentation of conservative treatment attempts',
    required: false,
  },
  {
    id: 'injection-records',
    label: 'Injection Records',
    description: 'Documentation of injection therapy attempts',
    required: false,
  },
  {
    id: 'referral',
    label: 'Referral / Orders',
    description: 'Referring physician orders and referral documentation',
    required: false,
  },
  {
    id: 'policy-docs',
    label: 'Payer Policy Documentation',
    description: 'Relevant medical policy excerpts supporting coverage',
    required: false,
  },
  {
    id: 'literature',
    label: 'Medical Literature',
    description: 'Peer-reviewed articles supporting medical necessity',
    required: false,
  },
  {
    id: 'other',
    label: 'Other Supporting Documents',
    description: 'Any additional relevant documentation',
    required: false,
  },
];

export const AttachmentChecklist: React.FC<AttachmentChecklistProps> = ({
  value,
  onChange,
  procedureCategory,
  denialCategory,
  disabled = false,
}) => {
  // Initialize with defaults if empty
  const items = value.length > 0 
    ? value 
    : DEFAULT_ATTACHMENTS.map(a => ({ ...a, checked: false }));

  const handleToggle = (id: string) => {
    onChange(
      items.map(item =>
        item.id === id ? { ...item, checked: !item.checked } : item
      )
    );
  };

  // Filter items by relevance
  const relevantItems = items.filter(item => {
    if (!item.suggestedFor) return true;
    
    const { procedures, denials } = item.suggestedFor;
    
    if (procedures && procedureCategory && !procedures.includes(procedureCategory)) {
      return false;
    }
    if (denials && denialCategory && !denials.includes(denialCategory)) {
      return false;
    }
    return true;
  });

  const requiredItems = relevantItems.filter(i => i.required);
  const suggestedItems = relevantItems.filter(i => !i.required);

  const checkedCount = items.filter(i => i.checked).length;
  const requiredCheckedCount = requiredItems.filter(i => i.checked).length;
  const allRequiredChecked = requiredCheckedCount === requiredItems.length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <AttachIcon color="primary" />
        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
          Attachment Checklist
        </Typography>
        <Tooltip title="Verify you have gathered all necessary supporting documentation before finalizing the appeal. Check items as you attach them.">
          <InfoIcon fontSize="small" color="action" />
        </Tooltip>
      </Stack>

      {/* Status banner */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2,
          backgroundColor: allRequiredChecked ? '#f0fdf4' : '#fef3c7',
          border: `1px solid ${allRequiredChecked ? '#22c55e' : '#f59e0b'}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {allRequiredChecked ? (
            <CheckIcon color="success" fontSize="small" />
          ) : (
            <WarningIcon color="warning" fontSize="small" />
          )}
          <Typography variant="body2">
            {allRequiredChecked 
              ? `All required documents confirmed (${checkedCount} total)`
              : `${requiredItems.length - requiredCheckedCount} required document(s) not yet confirmed`
            }
          </Typography>
        </Stack>
      </Paper>

      {/* Required attachments */}
      <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
        Required Documents
      </Typography>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: '#fafafa',
          border: '1px solid #e2e8f0',
        }}
      >
        <FormGroup>
          {requiredItems.map((item) => (
            <FormControlLabel
              key={item.id}
              control={
                <Checkbox
                  checked={item.checked}
                  onChange={() => handleToggle(item.id)}
                  disabled={disabled}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.label}
                    </Typography>
                    <Chip label="Required" size="small" color="error" variant="outlined" sx={{ height: 20 }} />
                  </Stack>
                  <Typography variant="caption" color="textSecondary">
                    {item.description}
                  </Typography>
                </Box>
              }
              sx={{
                mb: 1,
                alignItems: 'flex-start',
                '& .MuiCheckbox-root': { pt: 0 },
              }}
            />
          ))}
        </FormGroup>
      </Paper>

      {/* Suggested attachments */}
      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
        Suggested Documents
      </Typography>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: '#fafafa',
          border: '1px solid #e2e8f0',
        }}
      >
        <FormGroup>
          {suggestedItems.map((item) => (
            <FormControlLabel
              key={item.id}
              control={
                <Checkbox
                  checked={item.checked}
                  onChange={() => handleToggle(item.id)}
                  disabled={disabled}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {item.description}
                  </Typography>
                </Box>
              }
              sx={{
                mb: 1,
                alignItems: 'flex-start',
                '& .MuiCheckbox-root': { pt: 0 },
              }}
            />
          ))}
        </FormGroup>
      </Paper>

      <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
        Note: This checklist is a reminder to gather documents. The actual attachments should be 
        compiled separately and included with the printed/submitted appeal letter.
      </Typography>
    </Box>
  );
};

export const getDefaultAttachments = (
  procedureCategory?: ProcedureCategory,
  denialCategory?: DenialCategory
): AttachmentItem[] => {
  return DEFAULT_ATTACHMENTS.map(item => {
    // Auto-check items that are particularly relevant
    let autoCheck = item.required;
    
    if (item.suggestedFor) {
      const { procedures, denials } = item.suggestedFor;
      if (procedures && procedureCategory && procedures.includes(procedureCategory)) {
        autoCheck = true;
      }
      if (denials && denialCategory && denials.includes(denialCategory)) {
        autoCheck = true;
      }
    }
    
    return { ...item, checked: autoCheck };
  });
};
